/**
 * 활성 카테고리 헤더.
 *
 * 우측 컨텐츠 영역 최상단에 노출 — 현재 카테고리가 무엇인지(아이콘, 한글명, 영문 코드),
 * 무엇을 보관해야 하는지(설명), 현재 보관된 파일이 몇 개인지(파일 수) + 총 용량을 함께 표시.
 */

import React from 'react';
import { Box, Typography, Chip, alpha, useTheme } from '@mui/material';
import type { CategoryMeta } from './categoryMeta';

interface CategoryHeaderProps {
  meta: CategoryMeta;
  /** 현재 카테고리의 파일 개수. */
  fileCount: number;
  /** 총 용량(바이트). null/undefined 이면 미표시. */
  totalBytes?: number | null;
  /** 카테고리별 특성을 한 줄로 — "버전 관리 지원", "CCTV별 폴더" 등. */
  capabilityLabel?: string;
}

/** 바이트 → 사람이 읽기 좋은 단위. */
function humanizeBytes(bytes: number): string {
  if (!bytes || bytes < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

const CategoryHeader: React.FC<CategoryHeaderProps> = ({
  meta,
  fileCount,
  totalBytes,
  capabilityLabel,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { label, code, description, Icon, accent } = meta;

  return (
    <Box
      sx={{
        mb: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: isDark ? alpha(accent, 0.3) : alpha(accent, 0.22),
        background: isDark
          ? `linear-gradient(135deg, ${alpha(accent, 0.15)} 0%, ${alpha('#000', 0.4)} 100%)`
          : `linear-gradient(135deg, ${alpha(accent, 0.1)} 0%, ${alpha('#fff', 0.92)} 100%)`,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: 1.5,
          bgcolor: accent,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 6px 18px ${alpha(accent, 0.3)}`,
        }}
      >
        <Icon fontSize="medium" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography
            sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '1.05rem', color: 'text.primary' }}
          >
            {label}
          </Typography>
          <Chip
            label={code}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.68rem',
              fontWeight: 600,
              letterSpacing: 0.5,
              bgcolor: alpha(accent, 0.18),
              color: accent,
              border: `1px solid ${alpha(accent, 0.3)}`,
            }}
          />
        </Box>
        <Typography
          variant="body2"
          sx={{
            mt: 0.25,
            color: 'text.secondary',
            lineHeight: 1.35,
            fontSize: '0.78rem',
          }}
        >
          {description}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <StatChip label="파일" value={`${fileCount.toLocaleString()}개`} accent={accent} />
        {typeof totalBytes === 'number' && totalBytes > 0 && (
          <StatChip label="용량" value={humanizeBytes(totalBytes)} accent={accent} />
        )}
        {capabilityLabel && (
          <Chip
            label={capabilityLabel}
            size="small"
            variant="outlined"
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 500,
              borderColor: alpha(accent, 0.4),
              color: accent,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

interface StatChipProps {
  label: string;
  value: string;
  accent: string;
}

const StatChip: React.FC<StatChipProps> = ({ label, value, accent }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box
      sx={{
        px: 1,
        py: 0.4,
        borderRadius: 1,
        bgcolor: isDark ? alpha('#000', 0.3) : alpha('#fff', 0.75),
        border: '1px solid',
        borderColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06),
        display: 'flex',
        alignItems: 'baseline',
        gap: 0.5,
        height: 24,
      }}
    >
      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: accent, lineHeight: 1 }}>
        {value}
      </Typography>
    </Box>
  );
};

export default CategoryHeader;
